import { useUser } from '~/hooks/useUser';
import { useApiKeysQuery, useCurrentUser } from '~/queries';
import { useActivityTemplatesQuery } from '~/queries/activity/useActivityTemplateQuery';
import { useCurrentOrganization } from '~/queries/organization/useOrganizationsQuery';
import { useProcessesQuery } from '~/queries/process/useProcessQuery';
import { useCurrentWorkspace } from '~/queries/workspace/useWorkspacesQuery';

/**
 * Workspace write permissions check hook
 */
export const useCanWriteWorkspace = () => {
  const { data: currentWorkspace } = useCurrentWorkspace();
  return currentWorkspace?.permissions?.includes('write_workspace') || false;
};

/**
 * Process related permission check hooks
 */
export const useCanEditProcess = () => {
  const { data: currentWorkspace } = useCurrentWorkspace();

  return currentWorkspace?.permissions?.includes('write_processes') || false;
};

export const useCanTriggerProcessWithEmail = () => {
  const { data: currentOrganization } = useCurrentOrganization();

  return currentOrganization?.limit_overrides?.canTriggerProcessesWithEmail || false;
};

export const useCanScheduleProcess = () => {
  const { data: currentOrganization } = useCurrentOrganization();

  return currentOrganization?.limit_overrides?.processSchedulerEnabled || false;
};

export const useCanViewOrganizationMembers = () => {
  const { data: currentOrganization } = useCurrentOrganization();
  return currentOrganization?.permissions?.includes('view_org_users') || false;
};

export const useCanManageOrganizationMembers = () => {
  const { data: currentOrganization } = useCurrentOrganization();
  const { data: currentUser } = useCurrentUser();

  if (currentUser?.limit_overrides.canManageOrganizations === false) {
    return false;
  }

  return currentOrganization?.permissions?.includes('manage_org_users') || false;
};

/**
 * Steps related permission check hooks
 */
export const useCanAddStepToProcess = (stepCount: number) => {
  const { data: currentWorkspace } = useCurrentWorkspace();

  return currentWorkspace?.limit_overrides.maxActivitiesInProcess
    ? stepCount < currentWorkspace.limit_overrides.maxActivitiesInProcess
    : false;
};

export const useCanAddStepToWorkspace = (stepCount: number) => {
  const { data: currentWorkspace } = useCurrentWorkspace();

  return currentWorkspace?.limit_overrides.maxActivitiesInWorkspace
    ? stepCount < currentWorkspace.limit_overrides.maxActivitiesInWorkspace
    : false;
};

export const useCanEnableVideoStreaming = () => {
  const { data: currentOrganization } = useCurrentOrganization();

  return currentOrganization?.limit_overrides?.videoStreamingEnabled || false;
};

/**
 * Robot read permissions check hook
 */
export const useCanReadRobots = () => {
  const { data: currentWorkspace } = useCurrentWorkspace();
  return currentWorkspace?.permissions?.includes('read_robots') || false;
};

/**
 * Robot read permissions check hook
 */
export const useCanWriteRobots = () => {
  const { data: currentWorkspace } = useCurrentWorkspace();
  return currentWorkspace?.permissions?.includes('write_robots') || false;
};

/**
 * Robots related permission check hooks
 */
export const useCanAddRobot = () => {
  const { data: currentWorkspace } = useCurrentWorkspace();
  const { data: allRobots = [] } = useActivityTemplatesQuery();

  return currentWorkspace?.limit_overrides.maxActivityTemplatesInWorkspace && allRobots
    ? allRobots.length < currentWorkspace?.limit_overrides.maxActivityTemplatesInWorkspace
    : false;
};

/**
 * Checks if new process can be added to workspace
 */
export const useCanAddProcess = () => {
  const { data: currentOrganization } = useCurrentOrganization();
  const { data: allWorkspaceProcesses = [] } = useProcessesQuery();

  // We currently make a distinction between undefined and null, the latter means unlimited
  const hasUnlimitedProcesses =
    currentOrganization?.limit_overrides?.maxProcessesPerWorkspace === null;
  if (hasUnlimitedProcesses) return { canAddProcess: true, limit: Infinity };

  const limit = currentOrganization?.limit_overrides?.maxProcessesPerWorkspace ?? 0;

  return { canAddProcess: allWorkspaceProcesses?.length < limit, limit };
};

/**
 * Vault related permissions
 */
export const useCanAddVaultSecrets = () => {
  const { data: currentWorkspace } = useCurrentWorkspace();
  return currentWorkspace?.permissions?.includes('write_vault') || false;
};

/**
 * Check if secret limit is reached for workspace
 */
export const useCanAddSecretToWorkspace = (secretCount: number) => {
  const { data: currentWorkspace } = useCurrentWorkspace();

  const maxSecretsInWorkspace = currentWorkspace?.limit_overrides?.maxSecretsInWorkspace;
  if (maxSecretsInWorkspace === null) return true;

  return maxSecretsInWorkspace ?? secretCount < 0;
};

/**
 * Check if secret limit is reached for workspace
 */
export const useCanViewSecretValue = () => {
  const { data: currentOrganization } = useCurrentOrganization();
  return !currentOrganization?.config.preventVaultSecretView;
};

/**
 * Assistant related permissions
 */
export const useCanWriteAssistants = () => {
  const { data: currentWorkspace } = useCurrentWorkspace();
  return currentWorkspace?.permissions?.includes('write_assistants') || false;
};

/**
 * Worker read permissions check hook
 */
export const useCanReadWorkers = () => {
  const { data: currentWorkspace } = useCurrentWorkspace();
  return currentWorkspace?.permissions?.includes('read_workers') || false;
};

export const useCanInsertWorkerToken = () => {
  const { data: currentWorkspace } = useCurrentWorkspace();
  return currentWorkspace?.permissions?.includes('insert_runtime_link_token') || false;
};

export const useCanManageWorkerGroups = () => {
  const { data: currentOrganization } = useCurrentOrganization();
  return !!currentOrganization?.limit_overrides?.environmentGroupsEnabled || false;
};

export const useCanManageOnDemandWorkers = () => {
  const { data: currentOrganization } = useCurrentOrganization();
  return !!currentOrganization?.limit_overrides?.selfHostedOndemandRuntimeEnvsEnabled || false;
};

/**
 * Asset write permissions check hook
 */
export const useCanWriteAssetStorageAsset = () => {
  const { data: currentWorkspace } = useCurrentWorkspace();
  return currentWorkspace?.permissions?.includes('write_asset_storage_assets') || false;
};

/**
 * User organization permissions check
 */
export const useCanCreateOrganization = () => {
  const user = useUser();

  const { maxOrganizations } = user.limit_overrides;
  const createdOrganizations = user.current_limited?.createdOrganizations ?? 0;

  return (maxOrganizations && createdOrganizations < maxOrganizations) || false;
};

/**
 * Work item data read permissions check hook
 */
export const useCanWriteWorkItemData = () => {
  const { data: currentWorkspace } = useCurrentWorkspace();
  return currentWorkspace?.permissions?.includes('write_work_item_data') || false;
};

/**
 * Checks if user can add api key
 */
export const useCanAddApiKey = () => {
  const user = useUser();
  const { data: apiKeys = [] } = useApiKeysQuery();

  const { maxAccessCredentials } = user.limit_overrides;
  return [
    maxAccessCredentials ? apiKeys.length < maxAccessCredentials : false,
    maxAccessCredentials,
  ];
};
